import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { MagnetDesign, SavedImageState } from '../home/models';

@Injectable({
  providedIn: 'root'
})
export class DesignQueueService {
  private designs: MagnetDesign[] = [];
  private currentDesignId: string | null = null;
  private designsSubject = new BehaviorSubject<MagnetDesign[]>([]);
  
  // Observable for components to subscribe to design changes
  designs$: Observable<MagnetDesign[]> = this.designsSubject.asObservable();

  /**
   * Get all designs (returns a copy to prevent direct mutation)
   */
  getDesigns(): MagnetDesign[] {
    return [...this.designs];
  }

  /**
   * Get a specific design by ID
   */
  getDesign(designId: string): MagnetDesign | undefined {
    return this.designs.find(d => d.id === designId);
  }

  /**
   * Get the current design being edited
   */
  getCurrentDesignId(): string | null {
    return this.currentDesignId;
  }

  /**
   * Set the current design ID (for editing existing designs)
   */
  setCurrentDesignId(designId: string | null): void {
    this.currentDesignId = designId;
  }

  /**
   * Generate a unique design ID
   */
  generateDesignId(): string {
    return `design-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Add a new design to the queue
   */
  addDesign(design: MagnetDesign): void {
    this.designs.push(design);
    this.notifySubscribers();
  }

  /**
   * Add or update a design (if currentDesignId is set, updates existing; otherwise adds new)
   */
  addOrUpdateDesign(design: MagnetDesign): void {
    if (this.currentDesignId) {
      const index = this.designs.findIndex(d => d.id === this.currentDesignId);
      if (index !== -1) {
        this.designs[index] = design;
      } else {
        this.designs.push(design);
      }
      this.currentDesignId = null;
    } else {
      this.designs.push(design);
    }
    this.notifySubscribers();
  }

  /**
   * Remove a design from the queue
   */
  removeDesign(designId: string): void {
    this.designs = this.designs.filter(d => d.id !== designId);
    this.notifySubscribers();
  }

  /**
   * Update the quantity of a specific design
   */
  updateDesignQuantity(designId: string, quantity: number): void {
    const design = this.designs.find(d => d.id === designId);
    if (design) {
      design.quantity = Math.max(1, quantity);
      this.notifySubscribers();
    }
  }

  /**
   * Update the location of a specific design
   */
  updateDesignLocation(designId: string, location: any): void {
    const design = this.designs.find(d => d.id === designId);
    if (design) {
      design.location = location;
      this.notifySubscribers();
    }
  }

  /**
   * Edit a design - removes it from queue and returns it for editing
   */
  editDesign(designId: string): MagnetDesign | null {
    const design = this.designs.find(d => d.id === designId);
    if (design) {
      // Store the design ID for later update
      this.currentDesignId = designId;
      
      // Remove from queue temporarily (will be re-added on finish)
      this.designs = this.designs.filter(d => d.id !== designId);
      this.notifySubscribers();
      
      return design;
    }
    return null;
  }

  /**
   * Calculate total quantity across all designs
   */
  getTotalQuantity(): number {
    return this.designs.reduce((sum, design) => sum + design.quantity, 0);
  }

  /**
   * Calculate total price based on quantity and price per unit
   */
  getTotalPrice(pricePerUnit: number): number {
    return this.getTotalQuantity() * pricePerUnit;
  }

  /**
   * Check if queue is empty
   */
  isEmpty(): boolean {
    return this.designs.length === 0;
  }

  /**
   * Get queue length
   */
  getLength(): number {
    return this.designs.length;
  }

  /**
   * Clear all designs from the queue
   */
  clear(): void {
    this.designs = [];
    this.currentDesignId = null;
    this.notifySubscribers();
  }

  /**
   * Get the last design in the queue (for preview)
   */
  getLastDesign(): MagnetDesign | null {
    return this.designs.length > 0 ? this.designs[this.designs.length - 1] : null;
  }

  /**
   * Notify subscribers of design changes
   */
  private notifySubscribers(): void {
    this.designsSubject.next([...this.designs]);
  }
}

